import classNames from 'classnames';
import { ChangeEvent, FC, ForwardedRef, forwardRef, useEffect, useState } from 'react';
import Icon from '../Icons/Icon';
import Tooltip from '../Tooltip/Tooltip';
import styles from './Dropdown.module.css';
import { DropdownProps } from './Dropdown.types';

const Dropdown = forwardRef<HTMLSelectElement, DropdownProps>(
  (
    {
      id,
      label,
      disabled,
      onChange,
      options,
      value,
      errorText,
      loading,
      className,
      labelStyle,
      placeholderText = loading === true ? 'Loading...' : 'Select',
      hidePlaceholder = false,
      disablePlaceholderSelection = true,
      redBackground = false,
      tooltipText,
      labelDetails,
      optionalLabelText,
      dataTestId,
      ...props
    },
    ref: ForwardedRef<HTMLSelectElement>,
  ) => {
    const [selectedValue, setSelectedValue] = useState<string | undefined>(value || 'placeholder');

    const optionsList = options?.map((option) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ));

    useEffect(() => {
      setSelectedValue(value);
    }, [value]);

    const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
      if (onChange) {
        onChange(event);
      }
      setSelectedValue(event.target.value);
    };

    return (
      <div className={classNames(styles.container, { [className!]: className !== undefined })}>
        <div className={styles.labelWrapper}>
          <div>
            <label className={classNames(styles.label, { [labelStyle!]: labelStyle !== undefined })} htmlFor={id}>
              {label}
            </label>
            {optionalLabelText && <label htmlFor={id} className={styles.optionalText}>{` ${optionalLabelText}`}</label>}
            <div className={styles.labelDetailsWrapper}>
              {labelDetails && <div className={styles.labelDetails}>{labelDetails}</div>}
              {tooltipText && labelDetails && <Tooltip content={tooltipText} position="top" size={15} />}
            </div>
          </div>
          {tooltipText && !labelDetails && <Tooltip content={tooltipText} position="top" size={15} />}
        </div>
        <div className={styles.dropdownWrapper}>
          <select
            className={redBackground ? styles.dropdownRedBackground : styles.dropdown}
            aria-label={label}
            ref={ref}
            id={id}
            value={selectedValue}
            disabled={disabled}
            onChange={handleChange}
            data-testid={dataTestId || 'Dropdown'}
            {...props}
          >
            {!hidePlaceholder && (
              <option value="placeholder" disabled={disablePlaceholderSelection}>
                {placeholderText}
              </option>
            )}
            {optionsList}
          </select>
          {loading === true ? (
            <div className={styles.iconLoadingWrapper}>
              <Icon className={styles.rotate} size={16} iconName="Loader" />
            </div>
          ) : (
            <div className={styles.iconWrapper}>
              <Icon iconName="ChevronDown" />
            </div>
          )}
        </div>
        {errorText !== undefined && (
          <div className={styles.errorText} aria-live="assertive">
            {errorText}
          </div>
        )}
      </div>
    );
  },
);

Dropdown.displayName = 'Dropdown';

export default Dropdown;
